import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Select, { FormatOptionLabelMeta, MultiValue, SingleValue, StylesConfig } from 'react-select';
import { ICellEditor, ICellEditorParams } from 'ag-grid-community';
import cn from 'classnames';

import DropdownIndicator from '@/components/common/Dropdown/components/DropdownIndicator';
import { useOnClickOutside } from '@/hooks/useOnClickOutside.hook';
import focusStyles from './dropdown-grid-styles.module.scss';

type HandleChangeOption<Option> = (
  selectedOption: SingleValue<Option>,
  applyChanges: (selectedOption: SingleValue<Option>) => void,
  cancelChanges: () => void
) => void;
export interface IAgGridDropdownEditorProps<Option> extends ICellEditorParams {
  value: Option;
  options: Option[];
  placeholder?: string;
  getOptionLabel: (option: Option) => string;
  getOptionValue: (option: Option) => any;
  isDisabled?: boolean;
  handleChangeOption?: HandleChangeOption<Option>;
  saveOnChange?: boolean;
  autofocus?: boolean;
  styles?: StylesConfig<Option>;
  isLoading?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  formatOptionLabel?: (option: Option, labelMeta: FormatOptionLabelMeta<Option>) => React.ReactNode;
}

function AgGridDropdownEditor<Option>(
  props: IAgGridDropdownEditorProps<Option>,
  ref: ForwardedRef<ICellEditor>
) {
  const {
    value,
    placeholder,
    getOptionLabel,
    getOptionValue,
    options,
    isDisabled,
    api,
    saveOnChange,
    autofocus,
    styles,
    handleChangeOption,
    isLoading,
    isSearchable,
    isClearable,
    formatOptionLabel
  } = props;

  const refContainer = useRef<HTMLDivElement | null>(null);
  const refDropdown = useRef<any>();
  const [currentOption, setCurrentOption] = useState<SingleValue<Option> | MultiValue<Option>>(value);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(true);

  const defaultPlaceholder = useMemo(() => {
    return value ? getOptionLabel(value) : 'Select';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useOnClickOutside(refContainer, () => {
    setIsDropdownOpen(false);
  });

  const applyChanges = (selectedOption: SingleValue<Option>) => {
    setCurrentOption(selectedOption);
    setIsDropdownOpen(false);
    refDropdown.current?.focus();

    if (saveOnChange) {
      setIsEditing(false);
    }
  };

  const cancelChanges = () => {
    setIsEditing(false);
    refDropdown.current?.focus();
  };

  const onChange = (selectedOption: SingleValue<Option> | MultiValue<Option>) => {
    const option = Array.isArray(selectedOption) ? selectedOption[0] : selectedOption;

    if (typeof handleChangeOption === 'function') {
      setIsDropdownOpen(false);
      handleChangeOption(option, applyChanges, cancelChanges);
    }
    else {
      applyChanges(option);
    }
  };

  const onMenuOpen = () => {
    setIsDropdownOpen(true);
  };

  useImperativeHandle(ref, () => ({
    getValue() {
      return currentOption || value;
    },

    focusIn() {
      refDropdown.current?.focus();
    },

    isPopup() {
      return false;
    },
  }));

  useEffect(() => {
    if (autofocus) {
      refDropdown.current.focus();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (!isEditing && saveOnChange) {
      api.stopEditing();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isEditing]);

  return (
    <div ref={refContainer}>
      <Select
        ref={refDropdown}
        options={options}
        className={cn('ag-dropdown', focusStyles['custom-focus'])}
        classNamePrefix="ag-dropdown"
        components={{ DropdownIndicator }}
        placeholder={placeholder ?? defaultPlaceholder}
        onChange={onChange}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        isSearchable={isSearchable}
        onMenuOpen={onMenuOpen}
        menuIsOpen={isDropdownOpen}
        menuPortalTarget={document.querySelector('body')}
        isDisabled={isDisabled && isLoading}
        styles={styles}
        value={currentOption}
        isLoading={isLoading}
        isClearable={isClearable}
        formatOptionLabel={formatOptionLabel}
      />
    </div>
  );
}

export default forwardRef(AgGridDropdownEditor);
